import { useState } from 'react';
import { Mail, Phone, MapPin, Sparkles, CheckCircle2, Send, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosClient from '../api/axiosClient';

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    schoolName: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const hasNumber = /\d/;
    if (hasNumber.test(formData.firstName) || hasNumber.test(formData.lastName)) {
      toast.error("Name fields should not contain numbers.");
      return;
    }
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Mobile number should be exactly 10 digits.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axiosClient.post('/enquiries', formData);
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        schoolName: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      console.error('Failed to submit enquiry', err);
      toast.error('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="section-wrapper" style={{ paddingBottom: '4rem' }}>
        <div className="grid-2">
          <div>
            <span className="section-label">
              <Sparkles size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
              Get in Touch
            </span>
            <h1 className="hero-gradient-text" style={{ fontSize: '3rem' }}>
              We're here to help your school succeed
            </h1>
            <p className="section-desc" style={{ fontSize: '1.2rem', marginBottom: '2.5rem' }}>
              Reach out to our team for sales, support, or onboarding assistance.
            </p>
          </div>

          <div className="hero-mockup-wrapper" style={{ padding: '2rem' }}>
            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'center', margin: 0 }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <Mail size={32} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>Fast Response</h3>
                <p style={{ margin: 0, fontSize: '0.95rem' }}>Our dedicated support and sales team responds quickly to every inquiry.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Cards Section */}
      <section style={{ borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', background: 'rgba(241, 245, 249, 0.2)' }}>
        <div className="section-wrapper">
          <div className="contact-detail-grid">
            <div className="premium-card contact-detail-card">
              <div className="icon-box">
                <Mail size={24} />
              </div>
              <h3>Email Us</h3>
              <p>Our team will respond within 24 hours.</p>
              <a href="mailto:admin@roririsoft.com">admin@roririsoft.com</a>
            </div>

            <div className="premium-card contact-detail-card">
              <div className="icon-box">
                <Phone size={24} />
              </div>
              <h3>Call Us</h3>
              <p>Mon-Fri from 9am to 6pm IST.</p>
              <a href="tel:+917338941579">+91 7338941579</a>
            </div>

            <div className="premium-card contact-detail-card">
              <div className="icon-box">
                <MapPin size={24} />
              </div>
              <h3>Visit Us</h3>
              <p>Would like to visit & say hello at our office.</p>
              <address style={{ fontStyle: 'normal', color: 'var(--light-gray)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                RORIRI IT PARK, Kalakad, Tirunelveli<br />Tamilnadu, India -  627502
              </address>
            </div>
          </div>
        </div>
      </section>

      {/* Form Message Section */}
      <section className="section-wrapper">
        <div className="grid-2">
          <div>
            <h2 className="section-title" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Send us a Message</h2>
            <p className="section-desc" style={{ marginBottom: '2.5rem' }}>
              Our team will respond to your inquiry within 1-2 business days.
            </p>

            <div className="form-card" style={{ padding: '2.5rem' }}>
              {isSubmitted ? (
                <div className="form-success-container" style={{ padding: '2rem 0' }}>
                  <div className="success-icon-circle">
                    <CheckCircle2 size={36} />
                  </div>
                  <h3>Message Sent!</h3>
                  <p style={{ marginTop: '0.5rem', marginBottom: '1.5rem', maxWidth: '350px' }}>
                    Thank you for reaching out. We have received your inquiry and will respond to you shortly.
                  </p>
                  <button className="btn btn-outline" onClick={() => setIsSubmitted(false)}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label className="form-label" htmlFor="firstName">First Name</label>
                      <input 
                        type="text" 
                        id="firstName" 
                        name="firstName" 
                        className="form-control" 
                        placeholder="First Name" 
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="lastName">Last Name</label>
                      <input 
                        type="text" 
                        id="lastName" 
                        name="lastName" 
                        className="form-control" 
                        placeholder="Last Name" 
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label className="form-label" htmlFor="email">Email Address</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email" 
                        className="form-control" 
                        placeholder="name@example.com" 
                        value={formData.email}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="phone">Phone Number</label>
                      <input 
                        type="tel" 
                        id="phone" 
                        name="phone" 
                        className="form-control" 
                        placeholder="+91 98765 43210" 
                        value={formData.phone}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label className="form-label" htmlFor="schoolName">School Name</label>
                      <input 
                        type="text" 
                        id="schoolName" 
                        name="schoolName" 
                        className="form-control" 
                        placeholder="School Name (Optional)" 
                        value={formData.schoolName}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="subject">Subject</label>
                      <input 
                        type="text" 
                        id="subject" 
                        name="subject" 
                        className="form-control" 
                        placeholder="Subject" 
                        value={formData.subject}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label className="form-label" htmlFor="message">Message</label>
                    <textarea 
                      id="message" 
                      name="message" 
                      rows="3" 
                      className="form-control" 
                      placeholder="Write your message here..." 
                      value={formData.message}
                      onChange={handleInputChange}
                      required 
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send size={16} />
                  </button>
                </form>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="premium-card" style={{ width: '100%', padding: '3rem' }}>
              <span className="section-label" style={{ marginBottom: '1.5rem' }}>Instant Support</span>
              <h3 style={{ fontSize: '1.8rem', marginBottom: '1rem', fontFamily: 'var(--font-heading)' }}>Need help now?</h3>
              <p style={{ marginBottom: '2rem', lineHeight: '1.7' }}>
                Contact our customer sales and technical support teams for quick assistance with pricing, product setup, onboarding tutorial videos, or database migration from legacy platforms.
              </p>
              <a href="mailto:admin@roririsoft.com" className="btn btn-outline btn-lg btn-block">
                Email Support
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
